import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { Button } from "@/components/flexkit/Button";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { Tooltip } from "@/components/Tooltip";
import { Text } from "@/components/podkit/typography/Text";
import { Markdown } from "@/components/ai/chat/Markdown";
import type { AgentMessageFragmentCodeAnnotation } from "@/hooks/use-stream-conversation";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { Check, Copy } from "lucide-react";
import type React from "react";
import { useCallback, type FC } from "react";

type FragmentCodeAnnotationProps = {
    fragment: AgentMessageFragmentCodeAnnotation;
    onClick?: (fragment: AgentMessageFragmentCodeAnnotation) => void;
};

export const FragmentCodeAnnotation: FC<FragmentCodeAnnotationProps> = ({ fragment, onClick }) => {
    const getTitle = (): string => {
        return fragment.title || "Code Annotation";
    };

    const lineRange =
        fragment.startLine === fragment.endLine
            ? `Line ${fragment.startLine}`
            : `Lines ${fragment.startLine}-${fragment.endLine}`;

    return (
        <div className="flex flex-col overflow-hidden rounded-lg border border-border-base">
            <FragmentCodeAnnotationHeader
                fragment={fragment}
                title={getTitle()}
                lineRange={lineRange}
                onFileClick={() => onClick?.(fragment)}
            />
            <FragmentCodeAnnotationContent fragment={fragment} />
        </div>
    );
};

const FragmentCodeAnnotationHeader: FC<{
    fragment: AgentMessageFragmentCodeAnnotation;
    title: string;
    lineRange: string;
    onFileClick?: () => void;
}> = ({ fragment, title, lineRange, onFileClick }) => {
    const [copied, setCopied] = useTemporaryState(false, 2000);

    const handleCopyPath = useCallback(
        async (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            try {
                await navigator.clipboard.writeText(fragment.filePath);
                setCopied(true);
            } catch (error) {
                setCopied(false);
                toast({
                    title: "Failed to copy file path",
                    description: formatError(error),
                });
            }
        },
        [fragment.filePath, setCopied],
    );

    return (
        <div className="flex items-start gap-2 bg-surface-02 p-2">
            <IconInfo size="base" className="mt-0.5 shrink-0 text-content-secondary" />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                <Text className="truncate text-sm font-bold text-content-secondary">{title}</Text>
                <div className="group/path flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-1">
                        {onFileClick ? (
                            <button
                                className="min-w-0 cursor-pointer truncate border-none bg-transparent p-0 text-left text-sm font-bold text-content-secondary hover:text-content-link hover:underline"
                                onClick={onFileClick}
                                data-tracking-id="click-code-annotation-file-path"
                            >
                                {fragment.filePath}
                            </button>
                        ) : (
                            <Text className="select-text truncate text-sm font-bold text-content-secondary">
                                {fragment.filePath}
                            </Text>
                        )}
                        <Tooltip content={copied ? "Copied!" : "Copy file path"}>
                            <Button
                                variant="ghost"
                                size="xs"
                                LeadingIcon={() =>
                                    copied ? (
                                        <Check className="text-content-positive" size={14} />
                                    ) : (
                                        <Copy size={14} />
                                    )
                                }
                                className="shrink-0 opacity-0 transition-opacity group-hover/path:opacity-100"
                                onClick={handleCopyPath}
                                aria-label="Copy file path"
                                title="Copy file path"
                                data-tracking-id="copy-annotation-file-path"
                            />
                        </Tooltip>
                    </div>
                    <Text className="shrink-0 text-xs text-content-tertiary">{lineRange}</Text>
                </div>
            </div>
        </div>
    );
};

const FragmentCodeAnnotationContent: FC<{
    fragment: AgentMessageFragmentCodeAnnotation;
}> = ({ fragment }) => {
    return (
        <ScrollArea orientation="both" className="flex max-h-[200px] border-t border-border-base p-0">
            <div className="p-3">
                <Markdown content={fragment.description} className="text-content-primary" />
            </div>
        </ScrollArea>
    );
};
